/**
 * N28 « Prochaine action » (§ 4.2.2, § 4.3.1 : à droite du fil d'Ariane, au niveau Projet).
 * Calcul client sur les lectures du projet (`useDonneesProjet`), la boîte d’attention servie
 * (`refusal_code`) et la gouvernance du `/kpi` (`oldest_pending_approval_s`) ; règles pures dans
 * `lib/prochaineAction.ts`. Le profil affiché est celui de l'accueil (`useProfil`) ; la vue
 * combinée prend le premier profil cumulé.
 */
import type { JSX } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'

import type { ProfileId } from '@/api/client'
import { agregatsApi, type ElementAttention } from '@/api/agregatsApi'
import { api } from '@/api/client'
import { useDonneesProjet, type DonneesProjet } from '@/hooks/useDonneesProjet'
import { useProfil } from '@/hooks/useProfil'
import { useSession } from '@/hooks/useSession'
import { useWorkspaceOptionnel } from '@/hooks/useWorkspace'
import { useT } from '@/i18n/language'
import { buttonVariants } from '@/components/ui/button'
import {
  ACTIONS_N28, LIBELLES_ROLE_N28, NATURES_N28, REGLES, candidatsDepuisDonnees, depuisLisible, prochaineAction,
  type Candidat, type DonneesN28, type ResultatN28, type RoleN28,
} from '@/lib/prochaineAction'
import { LIEN_EN_TEXTE } from '@/lib/cibles'

/** Lectures de la passerelle propres à N28 ; absentes (`null`), le calcul garde ses lectures locales. */
export interface ServeurN28 {
  /** Boîte d'attention avec `refusal_code` ; remplace la file HITL quand elle répond. */
  attention?: readonly ElementAttention[] | null | undefined
  plusAncienneApprobationS?: number | null | undefined
  /** Nom du projet courant (libellé humain d'une décision sur un artefact). */
  projet?: string | undefined
  /** O37 : file de décisions servie (`view=mine`) ; absente (`undefined`) = repli historique. */
  decisions?: DonneesN28['decisions']
}

/** Le calcul N28 à partir des lectures du projet, à l'instant `maintenant` (ARB-r2-20). */
export function calculerN28(donnees: DonneesProjet, role: RoleN28, maintenant: Date, moi: string | undefined, serveur: ServeurN28 = {}): ResultatN28 {
  return prochaineAction(role, candidatsDepuisDonnees({
    maintenant, moi,
    attention: serveur.attention ?? donnees.attention.data,
    plusAncienneApprobationS: serveur.plusAncienneApprobationS,
    projet: serveur.projet,
    decisions: serveur.decisions,
    campagnes: donnees.campagnes.data,
    deploiements: donnees.deploiements.data,
    versions: donnees.versions.data?.items,
    apercus: donnees.apercus.data?.items,
    items: donnees.items.data,
    budgetsErreur: donnees.budgets.data,
  }))
}

/**
 * Lot RP (manque 24) : les trois profils ajoutés n'ont pas de règles N28 propres ; ils prennent
 * celles du rôle le plus proche — le Scrum Master pilote le sprint (PO), l'architecte relit ce que
 * la colonie a produit (développeur), le sponsor décide en registre métier (métier).
 */
const VERS_N28: Partial<Record<ProfileId, RoleN28>> = { scrum_master: 'po', architecte: 'dev', sponsor: 'metier' }

export function roleN28(vue: string, profils: readonly ProfileId[]): RoleN28 {
  const p = vue === 'combinee' ? (profils[0] ?? 'po') : vue
  return VERS_N28[p as ProfileId] ?? (p as RoleN28)
}

type Traduire = (cle: string, repli: string, vars?: Record<string, string | number>) => string
const sansTraduction: Traduire = (_cle, repli, vars) =>
  repli.replace(/\{\{(\w+)\}\}/g, (_m, v: string) => String(vars?.[v] ?? ''))

/** Rôle N28 dans la langue de l'écran (`n28.role.<rôle>`). */
export function libelleRole(role: RoleN28, t: Traduire = sansTraduction): string {
  return t(`n28.role.${role}`, LIBELLES_ROLE_N28[role])
}

/** Phrase d'action dans la langue de l'écran (`n28.action.<nom>`) ; repli sur le libellé calculé. */
export function libelleAction(c: Candidat, t: Traduire = sansTraduction): string {
  if (!c.action) return c.libelle
  const vars = { ...c.vars }
  const nature = vars['nature']
  if (typeof nature === 'string' && nature in NATURES_N28) {
    vars['nature'] = t(`n28.nature.${nature}`, NATURES_N28[nature as keyof typeof NATURES_N28])
  }
  return t(`n28.action.${c.action}`, ACTIONS_N28[c.action], vars).replace(/\s+/g, ' ').trim()
}

/** Texte « règle n : … » d'une action retenue. */
export function texteRegle(r: ResultatN28, t: Traduire = sansTraduction): string {
  if (!r.action) return ''
  const n = r.action.regle
  const regle = t(`n28.regle.${r.role}.${String(n).replace('.', '_')}`, REGLES[r.role][n] ?? '')
  const base = t('n28.regleN', 'règle {{n}} : {{regle}}', { n: n === 2.5 ? 'AGILE-2-01' : n, regle })
  return r.sautees.length > 0
    ? t('n28.regleSautees', '{{base}} ; {{n}} action(s) sautée(s) : refusée(s) avant le clic', { base, n: r.sautees.length })
    : base
}

export function ProchaineAction({ workspaceId, variante = 'barre' }: {
  workspaceId: string | null | undefined
  /** `pastille` (U6b vague 9, téléphone) : « Prochaine action › » jamais tronquée, le libellé
   *  complet dans le nom accessible (planche CoquilleTelephone). */
  variante?: 'barre' | 'bloc' | 'pastille'
}): JSX.Element | null {
  const { t } = useT()
  const session = useSession()
  const { vue, profils } = useProfil()
  const donnees = useDonneesProjet(workspaceId)
  const workspaces = useWorkspaceOptionnel()?.workspaces ?? []
  const role = roleN28(vue, profils)
  // L'instant représenté est celui du rendu : chaque lecture reçue relance le calcul (ARB-r2-20).
  const ws = workspaceId ?? ''
  const attention = useQuery({ queryKey: ['attention-projet', ws], queryFn: () => agregatsApi.attentionProjet(ws), enabled: Boolean(workspaceId), retry: false })
  const kpi = useQuery({ queryKey: ['kpi-gouvernance', ws], queryFn: () => agregatsApi.gouvernanceKpi(ws), enabled: Boolean(workspaceId), retry: false })
  // O37 : la même file que `next_step` (têtes de lignée) ; en échec, repli historique.
  const decisions = useQuery({ queryKey: ['approvals', ws, 'n28-mine'], queryFn: () => api.listApprovals(ws, 'pending', 'mine'), enabled: Boolean(workspaceId), retry: false })
  const maintenant = new Date()
  if (!workspaceId) return null
  const r = calculerN28(donnees, role, maintenant, session?.userId, {
    attention: attention.data?.elements,
    plusAncienneApprobationS: kpi.data?.governance?.oldest_pending_approval_s,
    projet: workspaces.find((w) => w.workspace_id === workspaceId)?.name,
    decisions: decisions.isSuccess ? decisions.data : undefined,
  })

  // N-33 : `depuisLisible` rend '' pour une date illisible ; sans durée, le fragment « en attente
  // depuis » disparaît du texte comme du nom accessible, au lieu de rester pendant.
  const depuis = r.action ? depuisLisible(r.action.depuis, maintenant) : ''

  if (variante === 'pastille') {
    if (!r.action) return null
    const role = libelleRole(r.role, t)
    const action = libelleAction(r.action, t)
    const nom = depuis
      ? t('n28.pastilleAria', 'Prochaine action pour votre rôle ({{role}}) : {{action}}, en attente depuis {{depuis}}', { role, action, depuis })
      : t('n28.pastilleAriaSansDuree', 'Prochaine action pour votre rôle ({{role}}) : {{action}}', { role, action })
    return (
      <Link to={r.action.lien} data-testid="n28-pastille" aria-label={nom} title={texteRegle(r, t)}
            className="inline-flex min-h-11 shrink-0 items-center gap-1 whitespace-nowrap rounded-full border bg-background px-3 text-sm font-medium hover:bg-muted">
        {t('n28.titre', 'Prochaine action')}
        <ArrowRight className="size-4 shrink-0" aria-hidden="true" />
      </Link>
    )
  }

  if (variante === 'barre') {
    if (!r.action) return null
    return (
      <Link to={r.action.lien} data-testid="n28-barre" title={texteRegle(r, t)}
            className="hidden min-h-11 min-w-0 max-w-[36rem] shrink items-center gap-2 rounded-lg border border-brand-text/30 bg-brand-subtle px-3 text-[0.8125rem] text-foreground hover:border-brand-text md:inline-flex">
        <ArrowRight className="size-4 shrink-0 text-brand-text" aria-hidden="true" />
        <span className="shrink-0 font-semibold text-brand-text">{t('n28.titre', 'Prochaine action')}</span>
        <span className="truncate font-medium">{libelleAction(r.action, t)}</span>
        {depuis ? <span className="hidden shrink-0 text-xs text-muted-foreground xl:inline">· {t('n28.enAttente', 'en attente depuis')} {depuis}</span> : null}
      </Link>
    )
  }

  return (
    <div data-testid="n28-bloc" className="space-y-2">
      <p className="text-xs text-muted-foreground">{libelleRole(r.role, t)}</p>
      {r.action ? (
        <>
          <p className="font-medium" data-testid="n28-libelle">
            {libelleAction(r.action, t)}
            {depuis ? <>{' '}<span className="font-normal text-muted-foreground">({t('n28.enAttente', 'en attente depuis')} {depuis})</span></> : null}
          </p>
          <Link to={r.action.lien} className={buttonVariants({ className: 'w-fit' })}>
            <ArrowRight aria-hidden="true" /> {t('n28.agir', 'Y aller')}
          </Link>
          {r.action.chemin ? (
            <p className="text-xs text-muted-foreground" data-testid="n28-chemin">
              {t('n28.objet', 'objet :')}{' '}
              <Link to={r.action.lien} className={`break-all font-mono ${LIEN_EN_TEXTE}`}>{r.action.chemin}</Link>
            </p>
          ) : null}
          <p className="text-xs text-muted-foreground" data-testid="n28-regle">
            {texteRegle(r, t)} · {t('n28.egalite', 'en cas d’égalité, la plus ancienne gagne')}
          </p>
        </>
      ) : (
        <>
          <p className="font-medium" data-testid="n28-rien">{t('n28.rien', 'Rien ne vous attend ici.')}</p>
          {r.autreRole ? (
            <p className="text-sm text-muted-foreground" data-testid="n28-autre-role">
              {t('n28.autreRole', 'Le {{role}} peut agir : {{action}}.', {
                role: libelleRole(r.autreRole.role, t),
                action: ((a) => a.charAt(0).toLowerCase() + a.slice(1))(libelleAction(r.autreRole, t)),
              })}
            </p>
          ) : null}
        </>
      )}
    </div>
  )
}
